// The task ROW's non-component half — its list class, its sub-line and its order. Split out of
// `TaskListRow.tsx` so that file exports components alone (react-refresh), and because these three are the
// parts a caller REACHES FOR: a surface picks its sub-line composition and whether it re-sorts, and gets the
// row itself unchanged.
import clsx from "clsx";
import type { components } from "@cntrl/api-client";
import { isOverdue, type TaskSummary } from "../../app/hooks/useTasks.js";
import styles from "./TaskListRow.module.scss";

/** The `<ul>` `TaskListRow`s go in — the bare reset. Compose the caller's own scroll/bleed class beside it. */
export function taskRowsClassName(...extra: (string | false | null | undefined)[]): string {
  return clsx(styles.rows, ...extra);
}

/**
 * The row's ONE sub-line, under the title: the release a rollout task belongs to, then a preview of its
 * notes, whichever of the two exist. They share a line rather than stacking because a row is two lines tall —
 * and they answer different questions ("which release?" / "what is this?"), so dropping either to make room
 * for the other loses something. It never wraps; the row's `text-overflow` cuts it, and the full text is on
 * the element's `title` for a hover.
 *
 * `release: false` for a surface that has ALREADY named the release — the releases hub's prep panel groups
 * its rows under a release head, where repeating the title on every row is the group's own name read six
 * times over.
 */
export function taskSubLine(task: TaskSummary, opts: { release?: boolean } = {}): string {
  const release = opts.release ?? true;
  return [release ? task.releaseTitle : null, task.notesPreview].filter(Boolean).join(" · ");
}

/**
 * Widget order: overdue first, then by due date (undated last), then title (A-Z).
 *
 * For a TRIAGE list only. The release checklist (08 To-do) deliberately does NOT use it: `artist.tasks`
 * carries `position`/`phase` and a rollout is read in the order the template laid it out, so overdue-first
 * would shuffle a plan out of its own sequence.
 */
export function sortTasksForList(items: readonly TaskSummary[]): TaskSummary[] {
  return [...items].sort((a, b) => {
    const ao = isOverdue(a.dueDate, a.status) ? 0 : 1;
    const bo = isOverdue(b.dueDate, b.status) ? 0 : 1;
    if (ao !== bo) return ao - bo;
    const ad = a.dueDate ?? "9999-12-31";
    const bd = b.dueDate ?? "9999-12-31";
    if (ad !== bd) return ad < bd ? -1 : 1;
    return (a.title ?? "").localeCompare(b.title ?? "");
  });
}

/** A roster task line in the shape the shared task row reads. */
export function asTaskSummary(t: components["schemas"]["RosterTaskLine"]): TaskSummary {
  return {
    taskId: t.taskId,
    bandId: t.bandId,
    title: t.title,
    status: t.status,
    dueDate: t.dueDate,
    initiatorPersonId: t.initiatorPersonId ?? undefined,
    assignees: (t.assigneePersonIds ?? []).map((personId) => ({
      personId,
      assignedByPersonId: t.initiatorPersonId ?? null,
      assignedOn: "",
    })),
  } as TaskSummary;
}
